import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const BUTTON_STEP = 0.2;

const clampZoom = (zoom: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

type DocumentZoomActions = {
  /** Adds the step to the current zoom, kept within 100%–400%. */
  zoomBy: (step: number) => void;
  reset: () => void;
};

const DocumentZoomContext = createContext<number | null>(null);
const DocumentZoomActionsContext = createContext<DocumentZoomActions | null>(null);

/** The zoom of one shown document: a new reset key brings it back to 100%. */
export const useDocumentZoomState = (resetKey: string) => {
  const [state, setState] = useState({ resetKey, zoom: MIN_ZOOM });
  // A new key shows 100% in this same render: the document never draws once at the zoom of the previous key.
  if (state.resetKey !== resetKey) setState({ resetKey, zoom: MIN_ZOOM });

  const actions = useMemo<DocumentZoomActions>(
    () => ({
      zoomBy: (step) => setState((previous) => ({ ...previous, zoom: clampZoom(previous.zoom + step) })),
      reset: () => setState((previous) => ({ ...previous, zoom: MIN_ZOOM })),
    }),
    [],
  );
  return { zoom: state.zoom, actions };
};

/** The zoom and its actions sit in two contexts, so a reader of the actions alone does not render on a zoom. */
export const DocumentZoomProvider = ({ resetKey, children }: { resetKey: string; children: ReactNode }) => {
  const { zoom, actions } = useDocumentZoomState(resetKey);
  return (
    <DocumentZoomActionsContext.Provider value={actions}>
      <DocumentZoomContext.Provider value={zoom}>{children}</DocumentZoomContext.Provider>
    </DocumentZoomActionsContext.Provider>
  );
};

const required = <T,>(value: T | null, hook: string): T => {
  if (value === null) throw new Error(`${hook} is used outside DocumentZoomProvider`);
  return value;
};

export const useDocumentZoom = () => required(useContext(DocumentZoomContext), 'useDocumentZoom');

export const useDocumentZoomActions = () => required(useContext(DocumentZoomActionsContext), 'useDocumentZoomActions');

/** The props of the zoom buttons. */
export const useZoomButtons = (zoom: number, { zoomBy, reset }: DocumentZoomActions) => {
  const onZoom = useCallback((direction: number) => zoomBy(direction * BUTTON_STEP), [zoomBy]);
  const getIsDisabledZoomButton = useCallback(
    (direction: number) => (direction > 0 ? zoom >= MAX_ZOOM : zoom <= MIN_ZOOM),
    [zoom],
  );
  return { onZoom, onReset: reset, getIsDisabledZoomButton };
};

export const useDocumentZoomButtons = () => useZoomButtons(useDocumentZoom(), useDocumentZoomActions());
